import { IPartCategorySubPartLink } from "../models/PartCategorySubPart";
import { subPartIdsByMainId } from "./mainVsSubParts";

/** A complete engine by name - "Engine", "Diesel Engine", "Export Engine" and so on. */
export const isEngineCategoryName = (name: string | null | undefined): boolean => {
  const normalized = (name ?? "").trim().toLowerCase();
  return normalized === "engine" || normalized.endsWith(" engine");
};

/**
 * Complete-engine categories that mustn't be sold because triage found the engine non-functional.
 * Export engines are included: a dead engine isn't sold whole by any route.
 */
export function blockedEngineCategoryIds(
  categories: { id: string; name: string | null | undefined }[],
  engineNonFunctional: boolean
): Set<string> {
  if (!engineNonFunctional) return new Set();
  return new Set(categories.filter((c) => isEngineCategoryName(c.name)).map((c) => c.id));
}

/** The configured sub-parts of the blocked engines - the reusable bits to sell individually. */
export function engineSubComponentCategoryIds(
  links: IPartCategorySubPartLink[],
  blockedIds: Set<string>
): Set<string> {
  const subComponents = new Set<string>();
  if (blockedIds.size === 0) return subComponents;

  subPartIdsByMainId(links).forEach((subPartIds, mainId) => {
    if (blockedIds.has(mainId)) subPartIds.forEach((id) => subComponents.add(id));
  });

  // An engine can't be a sub-component of itself or another blocked engine
  blockedIds.forEach((id) => subComponents.delete(id));
  return subComponents;
}

/**
 * The price map without the blocked engines. `mainVsSubPartsLosers` skips a main it has no price
 * for, so a blocked engine's sub-parts are never left out in its favour - however much it's bid.
 */
export function withoutBlocked<T>(priceByCategoryId: Map<string, T>, blockedIds: Set<string>): Map<string, T> {
  if (blockedIds.size === 0) return priceByCategoryId;
  return new Map(Array.from(priceByCategoryId).filter(([id]) => !blockedIds.has(id)));
}
